import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  fetchSubscriptionStatus, 
  fetchSubscriptionPlans, 
  startFreeTrial, 
  createCheckoutSession,
  checkCheckoutStatus,
  createPortalSession
} from '../lib/api';
import { 
  Check, 
  Crown, 
  Zap, 
  Star, 
  Clock, 
  CreditCard, 
  Loader2, 
  ArrowLeft,
  Sparkles,
  Shield,
  RefreshCw,
  LogOut
} from 'lucide-react';

export default function Subscription() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(14);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMessage, setPaymentMessage] = useState(null);
  const [loadingPortal, setLoadingPortal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Check for returning from Stripe checkout
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [status, plansData] = await Promise.all([
        fetchSubscriptionStatus(),
        fetchSubscriptionPlans()
      ]);
      setSubscriptionStatus(status);
      setPlans(plansData.plans || []);
      setTrialDays(plansData.trial_days || 14);
    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentMessage({
        type: 'error',
        text: 'Payment status check timed out. Please check your email for confirmation or contact support.'
      });
      setProcessingPayment(false);
      return;
    }

    setProcessingPayment(true);
    setPaymentMessage({ type: 'pending', text: 'Verifying payment...' });

    try {
      const status = await checkCheckoutStatus(sessionId);
      
      if (status.payment_status === 'paid') {
        setPaymentMessage({
          type: 'success',
          text: 'Payment successful! Your subscription is now active.'
        });
        setProcessingPayment(false);
        // Refresh subscription status
        const newStatus = await fetchSubscriptionStatus();
        setSubscriptionStatus(newStatus);
        // Clear URL params
        window.history.replaceState({}, '', '/subscription');
        return;
      } else if (status.status === 'expired') {
        setPaymentMessage({
          type: 'error',
          text: 'Payment session expired. Please try again.'
        });
        setProcessingPayment(false);
        window.history.replaceState({}, '', '/subscription');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentMessage({
        type: 'error',
        text: 'Error checking payment status. Please try again.'
      });
      setProcessingPayment(false);
    }
  };

  const handleStartTrial = async () => {
    try {
      setLoading(true);
      // Trial now redirects to Stripe checkout with trial period
      const result = await startFreeTrial();
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch (error) {
      console.error('Error starting trial:', error);
      alert(error.response?.data?.detail || 'Failed to start trial');
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    try {
      setSelectedPlan(planId);
      setProcessingPayment(true);
      const { checkout_url } = await createCheckoutSession(planId);
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert(error.response?.data?.detail || 'Failed to start checkout');
      setProcessingPayment(false);
      setSelectedPlan(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'monthly': return <Zap className="h-6 w-6" />;
      case 'yearly': return <Star className="h-6 w-6" />;
      case 'lifetime': return <Crown className="h-6 w-6" />;
      default: return <CreditCard className="h-6 w-6" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'monthly': return isDark ? 'text-blue-400' : 'text-blue-600';
      case 'yearly': return isDark ? 'text-green-400' : 'text-green-600';
      case 'lifetime': return isDark ? 'text-amber-400' : 'text-amber-600';
      default: return isDark ? 'text-gray-400' : 'text-gray-600';
    }
  };

  const getPlanBorder = (planId) => {
    switch (planId) {
      case 'monthly': return isDark ? 'border-blue-500/30 hover:border-blue-500/60' : 'border-blue-200 hover:border-blue-400';
      case 'yearly': return isDark ? 'border-green-500/30 hover:border-green-500/60' : 'border-green-200 hover:border-green-400';
      case 'lifetime': return isDark ? 'border-amber-500/50 hover:border-amber-500' : 'border-amber-300 hover:border-amber-500';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-slate-900' : 'bg-gray-50'}`}>
        <Loader2 className={`h-8 w-8 animate-spin ${isDark ? 'text-cyan-400' : 'text-blue-600'}`} />
      </div>
    );
  }

  // User already has access
  if (subscriptionStatus?.has_access) {
    return (
      <div className={`min-h-screen py-12 px-4 ${isDark ? 'bg-slate-900' : 'bg-gray-50'}`}>
        <div className="max-w-2xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/rvu-tracker')}
            className={`mb-6 ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Tracker
          </Button>
          
          <Card className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <CardHeader className="text-center pb-2">
              <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                isDark ? 'bg-green-500/20' : 'bg-green-100'
              }`}>
                <Shield className={`h-8 w-8 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
              </div>
              <CardTitle className={`text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                You Have Full Access!
              </CardTitle>
              <CardDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                {subscriptionStatus.status === 'admin' && 'Admin accounts have unlimited access'}
                {subscriptionStatus.status === 'lifetime' && 'You have lifetime access to all features'}
                {subscriptionStatus.status === 'subscribed' && `Your ${subscriptionStatus.subscription_type} subscription is active`}
                {subscriptionStatus.status === 'trial' && `Free trial - ${subscriptionStatus.days_remaining} days remaining`}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center pt-4">
              <Badge className={`${
                subscriptionStatus.status === 'trial' 
                  ? (isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700')
                  : (isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700')
              }`}>
                {subscriptionStatus.status === 'admin' && 'Admin Access'}
                {subscriptionStatus.status === 'lifetime' && 'Lifetime Member'}
                {subscriptionStatus.status === 'subscribed' && 'Active Subscription'}
                {subscriptionStatus.status === 'trial' && 'Free Trial Active'}
              </Badge>
              
              {subscriptionStatus.status === 'trial' && (
                <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                  <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                    Your trial ends on {new Date(subscriptionStatus.trial_ends_at).toLocaleDateString()}
                  </p>
                  <p className={`text-xs mt-2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                    Subscribe now to keep your access after the trial ends
                  </p>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-center">
              <Button
                onClick={() => navigate('/rvu-tracker')}
                className={isDark 
                  ? 'bg-cyan-600 hover:bg-cyan-500 text-white' 
                  : 'bg-blue-600 hover:bg-blue-500 text-white'
                }
              >
                Go to Tracker
              </Button>
            </CardFooter>
          </Card>

          {/* Show upgrade options during trial */}
          {subscriptionStatus.status === 'trial' && (
            <div className="mt-8">
              <h3 className={`text-lg font-semibold mb-4 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Upgrade to Continue After Trial
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                {plans.map((plan) => (
                  <Card 
                    key={plan.id}
                    className={`cursor-pointer transition-all ${isDark ? 'bg-slate-800' : 'bg-white'} ${getPlanBorder(plan.id)}`}
                    onClick={() => handleSubscribe(plan.id)}
                  >
                    <CardContent className="pt-6 text-center">
                      <div className={getPlanColor(plan.id)}>{getPlanIcon(plan.id)}</div>
                      <h4 className={`font-semibold mt-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h4>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        ${plan.price}
                        <span className={`text-sm font-normal ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          {plan.period !== 'lifetime' ? `/${plan.period}` : ''}
                        </span>
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const handleOpenPortal = async () => {
    setLoadingPortal(true);
    try {
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not open billing portal. Please try again.');
    } finally {
      setLoadingPortal(false);
    }
  };

  // Payment failed — user needs to fix their card
  if (subscriptionStatus?.status === 'payment_failed') {
    return (
      <div className={`min-h-screen py-12 px-4 ${isDark ? 'bg-slate-900' : 'bg-gray-50'}`}>
        <div className="max-w-md mx-auto">
          <Card className={`${isDark ? 'bg-slate-800 border-red-500/40' : 'bg-white border-red-300'} border-2`}>
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-red-500/15">
                <CreditCard className="h-8 w-8 text-red-400" />
              </div>
              <CardTitle className={`text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Payment Failed
              </CardTitle>
              <CardDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                Your last payment was declined. Please update your payment method to restore access.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center pt-2 pb-2">
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                Click the button below to open your secure billing portal and update your card.
              </p>
            </CardContent>
            <CardFooter className="flex flex-col gap-3 pt-2">
              <Button
                onClick={handleOpenPortal}
                disabled={loadingPortal}
                className="w-full bg-red-600 hover:bg-red-500 text-white font-semibold"
              >
                {loadingPortal ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Opening portal...</>
                ) : (
                  <><CreditCard className="mr-2 h-4 w-4" /> Update Payment Method</>
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  // User needs to subscribe
  return (
    <div className={`min-h-screen py-12 px-4 ${isDark ? 'bg-slate-900' : 'bg-gray-50'}`}>
      <div className="max-w-5xl mx-auto">

        {/* Logout button — top right */}
        <div className="flex justify-end mb-4">
          <button
            data-testid="subscription-logout-btn"
            onClick={() => { logout(); navigate('/login'); }}
            className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
              isDark
                ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'
            }`}
          >
            <LogOut size={15} />
            Log out / Switch Account
          </button>
        </div>
        {/* Payment message */}
        {paymentMessage && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            paymentMessage.type === 'success' 
              ? (isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700')
              : paymentMessage.type === 'error'
              ? (isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700')
              : (isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700')
          }`}>
            {paymentMessage.type === 'pending' && <Loader2 className="h-5 w-5 animate-spin" />}
            {paymentMessage.type === 'success' && <Check className="h-5 w-5" />}
            {paymentMessage.text}
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-10">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
            isDark ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20' : 'bg-gradient-to-br from-blue-100 to-purple-100'
          }`}>
            <Sparkles className={`h-8 w-8 ${isDark ? 'text-cyan-400' : 'text-blue-600'}`} />
          </div>
          <h1 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Choose Your Plan
          </h1>
          <p className={`text-lg ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
            Unlock full access to Doc RVU Tracker
          </p>
        </div>

        {/* Free Trial Card */}
        {subscriptionStatus?.trial_available && (
          <Card className={`mb-8 ${isDark ? 'bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border-cyan-500/30' : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200'}`}>
            <CardContent className="py-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-full ${isDark ? 'bg-cyan-500/20' : 'bg-blue-100'}`}>
                    <Clock className={`h-6 w-6 ${isDark ? 'text-cyan-400' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <h3 className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Start Your {trialDays}-Day Free Trial
                    </h3>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                      Try all features free. Auto-enrolls to monthly ($9.99/mo) after trial.
                    </p>
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                      Cancel anytime before trial ends to avoid charges.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleStartTrial}
                  disabled={processingPayment || loading}
                  className={`${isDark 
                    ? 'bg-cyan-600 hover:bg-cyan-500 text-white' 
                    : 'bg-blue-600 hover:bg-blue-500 text-white'
                  } px-8`}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Start Free Trial'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Divider */}
        <div className="flex items-center gap-4 mb-8">
          <div className={`flex-1 h-px ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`} />
          <span className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
            Or choose a plan
          </span>
          <div className={`flex-1 h-px ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`} />
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative transition-all duration-300 ${
                isDark ? 'bg-slate-800' : 'bg-white'
              } ${getPlanBorder(plan.id)} ${
                plan.id === 'lifetime' ? 'md:scale-105 shadow-xl' : ''
              }`}
            >
              {plan.id === 'lifetime' && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark ? 'bg-amber-500 text-black' : 'bg-amber-400 text-amber-900'
                }`}>
                  BEST VALUE
                </div>
              )}
              
              <CardHeader className="text-center pb-2">
                <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                  isDark ? 'bg-slate-700' : 'bg-gray-100'
                } ${getPlanColor(plan.id)}`}>
                  {getPlanIcon(plan.id)}
                </div>
                <CardTitle className={`text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {plan.name}
                </CardTitle>
                <CardDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                  {plan.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="text-center">
                <div className="mb-4">
                  <span className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    ${plan.price}
                  </span>
                  {plan.period !== 'lifetime' && (
                    <span className={`text-lg ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      /{plan.period}
                    </span>
                  )}
                </div>
                
                <ul className={`space-y-2 text-left text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                  <li className="flex items-center gap-2">
                    <Check className={`h-4 w-4 ${getPlanColor(plan.id)}`} />
                    Unlimited shift tracking
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className={`h-4 w-4 ${getPlanColor(plan.id)}`} />
                    Full CPT code database
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className={`h-4 w-4 ${getPlanColor(plan.id)}`} />
                    Calendar & history views
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className={`h-4 w-4 ${getPlanColor(plan.id)}`} />
                    CSV export
                  </li>
                  {plan.id === 'lifetime' && (
                    <li className="flex items-center gap-2">
                      <Check className={`h-4 w-4 ${getPlanColor(plan.id)}`} />
                      <span className="font-semibold">Forever access - pay once!</span>
                    </li>
                  )}
                </ul>
              </CardContent>
              
              <CardFooter>
                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={processingPayment}
                  className={`w-full ${
                    plan.id === 'lifetime'
                      ? 'bg-amber-500 hover:bg-amber-400 text-black font-semibold'
                      : plan.id === 'yearly'
                      ? (isDark ? 'bg-green-600 hover:bg-green-500' : 'bg-green-600 hover:bg-green-500') + ' text-white'
                      : (isDark ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-600 hover:bg-blue-500') + ' text-white'
                  }`}
                >
                  {processingPayment && selectedPlan === plan.id ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="mr-2 h-4 w-4" />
                      {plan.id === 'lifetime' ? 'Get Lifetime Access' : 'Subscribe'}
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Footer note */}
        <p className={`text-center text-sm mt-8 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
          Secure payment powered by Stripe. Cancel subscriptions anytime from your account.
        </p>
      </div>
    </div>
  );
}
